import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { SAVE_AS_NEW_LIST_ENDPOINT } from 'api/ListsApi';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { ApiResponse, ApiError, post } from 'services/api';
import { Trans } from 'services/i18n';
import { link2, spacing } from 'ui/theme';
import { Link } from 'ui/links';
import { useTranslation } from '.';
import { useModalsContext } from '../contexts/modals';
import {
  SaveAsNewListParams,
  SaveAsNewDynamicListResponse,
  SaveAsNewStaticListResponse,
} from '../types';

const StyledHrefWrapper = styled.div`
  margin-top: ${spacing.half};
`;

const StyledHref = styled.a`
  ${link2};
  color: inherit;
  :hover {
    color: inherit;
  }
`;

const saveAsNewListMutation = (params: SaveAsNewListParams) =>
  post(SAVE_AS_NEW_LIST_ENDPOINT, params);

export const useSaveAsNewListMutation = () => {
  const { t } = useTranslation();
  const dispatchHook = useDispatch();
  const { hideModal: closeModal } = useModalsContext();

  const showSuccessToast = (name: string, id: number) => {
    dispatchHook(
      addPositiveToast(
        <Trans
          t={t}
          i18nKey="toast.newList.positive"
          components={{
            div: <StyledHrefWrapper />,
            href: <StyledHref href={`/segmentation?emailListId=${id}`} />,
            strong: <strong />,
          }}
          values={{ listName: name }}
        >
          Successfully saved &quot;<strong>{name}</strong>&quot;
          <StyledHrefWrapper>
            <Link variant="inherit" href={`/segmentation?emailListId=${id}`} useReactRouter>
              View list
            </Link>
          </StyledHrefWrapper>
        </Trans>,
      ),
    );
  };

  const onSuccess = (
    resp: ApiResponse<SaveAsNewDynamicListResponse | SaveAsNewStaticListResponse>,
    vars: SaveAsNewListParams,
  ) => {
    if (vars?.isDynamic) {
      const listId = (resp as unknown as SaveAsNewDynamicListResponse)?.listId;
      showSuccessToast(vars.name, listId);
    } else {
      const subLists = (resp as unknown as SaveAsNewStaticListResponse)?.subLists || [];
      subLists.forEach((list) => {
        showSuccessToast(list.name, list.id);
      });
    }
    closeModal();
  };

  const onError = (resp: ApiError) => {
    const errorType = resp?.response?.data?.error || t('toast.newList.negative.errTypGeneric');
    const errorMessage = resp?.response?.data?.message || t('toast.newList.negative.errMsgGeneric');
    const errorMsg = `${errorType}: ${errorMessage}`;
    dispatchHook(addNegativeToast(t('toast.newList.negative.message', { errorMsg })));
  };

  return useMutation<
    ApiResponse<SaveAsNewDynamicListResponse | SaveAsNewStaticListResponse>,
    ApiError,
    SaveAsNewListParams
  >({
    mutationFn: saveAsNewListMutation,
    onSuccess,
    onError,
  });
};
